/**
 * Moves an item in an array from one index to another.
 *
 * @param {Array<T>} array - The array to move the item in.
 * @param {number} startIndex - The index of the item to move.
 * @param {number} destinationIndex - The index to move the item to.
 * @returns {Array<T>} A new array with the item moved.
 */
export function moveArrayIndex<T>(
  array: Array<T>,
  startIndex: number,
  destinationIndex: number,
): Array<T> {
  if (startIndex === destinationIndex) return array;

  const copy = [...array];
  const [element] = copy.splice(startIndex, 1);
  if (destinationIndex > startIndex) {
    copy.splice(destinationIndex - 1, 0, element);
  } else {
    copy.splice(destinationIndex, 0, element);
  }

  return copy;
}

/**
 * Inserts an item into an array at a specified index.
 *
 * @param {Array<T>} array - The array to insert the item into.
 * @param {number} index - The index at which to insert the item.
 * @param {T} item - The item to insert.
 * @returns {Array<T>} A new array with the item inserted.
 */
export function insertIntoArray<T>(
  array: Array<T>,
  index: number,
  item: T,
): Array<T> {
  const copy = [...array];

  copy.splice(index, 0, item);

  return copy;
}

/**
 * Removes an item from an array at a specified index.
 *
 * @param {Array<T>} array - The array to remove the item from.
 * @param {number} index - The index at which to remove the item.
 * @returns {Array<T>} A new array with the item removed.
 */
export function removeFromArray<T>(array: Array<T>, index: number): Array<T> {
  const copy = [...array];

  copy.splice(index, 1);

  return copy;
}

/**
 * Expands an array at a specified index with another array.
 *
 * @param {Array<T>} sourceArray - The array to be inserted.
 * @param {Array<T>} destinationArray - The array where the sourceArray will be inserted.
 * @param {number} replacementIndex - The index at which to insert the sourceArray.
 * @returns {Array<T>} A new array with the sourceArray inserted at the replacementIndex.
 */
export function expandArrayAtIndex<T>(
  sourceArray: Array<T>,
  destinationArray: Array<T>,
  replacementIndex: number,
): Array<T> {
  destinationArray = destinationArray
    .slice(0, replacementIndex)
    .concat(sourceArray)
    .concat(destinationArray.slice(replacementIndex + 1));
  return destinationArray;
}
